/*
 * Copyright (c) 2021 CalAmp Corp. All Rights Reserved
 */
import React, {useCallback, useState} from 'react';
import MenuList from '@material-ui/core/MenuList';
import {makeStyles} from '@material-ui/core/styles';
import {isEscape, isTab} from '@ion/core/src/utils/KeyEventUtils';
import {toggleThemeStyle} from '@ion/dls/src/theme/helpers';
import {dlsColor, dlsTypography} from '@ion/dls/src/theme/theme';
import {DLSTheme} from '@ion/dls/src/types/types';
import DynamicLookUp from './DynamicLookUp';
import MultiSelectionSuggestions from './MultiSelectionSuggestions';
import {currentThemeSuggestionItemStyle} from './suggestionItemStyles';
import {
  IDatePickerState,
  SearchQueryValue,
  SearchSuggestionsProps,
  SuggestionsType
} from './types';
import {getDateSelection, isAnySuggestionType} from './utils';

export const useStyles = makeStyles((theme: DLSTheme) => ({
  // `overflow: 'scroll'` forces both scrollbars to show even when the list
  // fits without scrolling (e.g. Status' 3 short options) - `auto` only
  // shows a scrollbar when the content actually needs it. Current theme
  // also matches Dropdown.tsx's own `listbox` (its Autocomplete option
  // list) exactly - no padding/margin of its own, current/legacy's own
  // suggestionItem rows draw their own spacing instead ...
  listWrapper : toggleThemeStyle({
    height    : 'auto',
    maxHeight : '400px',
    overflow  : 'scroll'
  }, {
    height    : 'auto',
    maxHeight : '400px',
    overflowY : 'auto',
    overflowX : 'hidden',
    padding   : '0px !important',
    margin    : '0px !important'
  }),
  // current theme matches Dropdown.tsx's own `option` class exactly (same
  // padding/typography/color, and the same hover highlight color) rather
  // than the previous fixed-width pill design, which didn't match the
  // shared Dropdown component's real current-theme look ...
  suggestionItem : toggleThemeStyle({
    padding              : '0 12px !important',
    gap                  : 4,
    width                : (props) => props?.isFixedWidth ? 220 : 'auto',
    height               : 40,
    '&:not(:last-child)' : {
      background : `linear-gradient(${theme.dls.colors.grey4}, ${theme.dls.colors.grey4}) bottom/ 95% 1px no-repeat`
    },
    '&:hover, &:focus' : {
      background : theme.dls.colors.grey5
    }
  }, currentThemeSuggestionItemStyle()),
  // matches Dropdown.tsx's own option typography exactly - no fixed-width
  // truncation needed anymore now that suggestionItem no longer forces a
  // narrower-than-container box ...
  suggestionItemLabel : toggleThemeStyle({}, {
    ...dlsTypography('body2'),
    color         : dlsColor('grey1'),
    textTransform : 'none'
  }),
  searchWrapper : {
    boxShadow : '0px 3px 3px #e0e0e0'
  },
  dateMenuItem : {
    height          : ({hasDateField}: {hasDateField: boolean}) => hasDateField ? 'auto' : '150px',
    flexWrap        : 'wrap',
    display         : 'flex',
    margin          : ({hasDateField}: {hasDateField: boolean}) => hasDateField ? '0 12px' : '0 20px',
    gap             : '20px',
    backgroundColor : 'transparent'
  },
  menuSubmissionWrapperClass : {
    width          : ({hasDateField}: {hasDateField: boolean}) => hasDateField ? '100%' : '140px',
    display        : 'flex',
    justifyContent : 'flex-end',
    alignItems     : 'center',
    gap            : '15px',
    padding        : ({hasDateField}: {hasDateField: boolean}) => hasDateField ? '0' : '0 10px',
    height         : ({hasDateField}: {hasDateField: boolean}) => hasDateField ? 'auto' : '40px',
    marginLeft     : 'auto'
  },
  label : toggleThemeStyle({
    whiteSpace   : 'nowrap',
    overflow     : 'hidden',
    textOverflow : 'ellipsis'
  }, {
    color         : `${dlsColor('grey2')} !important`,
    fontSize      : '14px !important',
    overflow      : 'hidden',
    fontFamily    : 'Inter !important',
    fontWeight    : 500,
    lineHeight    : '20px !important',
    whiteSpace    : 'nowrap',
    textOverflow  : 'ellipsis',
    letterSpacing : '0px !important',
    textTransform : 'capitalize',
    '& span'      : {
      textTransform : 'capitalize'
    }
  }),
  selectedListItem : toggleThemeStyle({
    backgroundColor : `${dlsColor('lightBlue')} !important`,
    width           : (props) => props?.isFixedWidth ? 220 : 'auto'
  }, {
    gap             : '8px',
    width           : '254px',
    height          : '44px',
    display         : 'flex',
    padding         : '17px !important',
    overflow        : 'hidden',
    paddingLeft     : '8px',
    borderRadius    : '8px',
    paddingRight    : '8px',
    backgroundColor : `${dlsColor('lightBlue')} !important`
  }),
  favouritesActions : {
    gap             : '8px',
    display         : 'inline-grid',
    padding         : '16px',
    backgroundColor : dlsColor('grey13')
  }
}) as any);

/**
 * Component constants
 */
const MenuListID = 'suggestion-menu';

/**
 * Defines function returns page name from url
 * @param url string
 * @returns string
 *
 * ```typescript
 * import {SearchSuggestions} from '@ion/features/src/search/';
 * <SearchSuggestions handleListKeyDown={handleListKeyDown}
 *   selectedSuggestions={selectedSuggestions}
 *   query={searchQuery}
 *   menuAutoFocus={true}
 *   onMenuSelection={onMenuSelection}
 *   menuSelectionType={MULTISELECTION}
 *   listOptions={listOPtions}
 *   attributeMappings={searchInstance.mappings} />
 * ```
 */

export const SearchSuggestions = ({
  listOptions,
  attributeMappings,
  onMenuSelection,
  menuAutoFocus,
  menuSelectionType,
  selectedSuggestions,
  menuListRef,
  lookUp,
  query,
  closeDropdown,
  inputEventTrigger,
  updateFilterLookUpData,
  inputValue,
  isCaseInsensitive = false,
  accountTree,
  setShowSuggestions,
  hasDateField = false
}: SearchSuggestionsProps) => {
  const classes               = useStyles({hasDateField});
  const [isLast, refreshLast] = useState<boolean>(false);

  const onSelection = useCallback(function(item: string | SearchQueryValue | IDatePickerState[]) {
    return (event?: any) => {
      event?.preventDefault();
      if (isAnySuggestionType(SuggestionsType.DATE)(menuSelectionType)) {
        onMenuSelection({
          label : item,
          value : getDateSelection(item)
        }, menuSelectionType, {canSubmit: true});
      } else {
        onMenuSelection(item, menuSelectionType, event);
      }
    };
  }, [menuSelectionType, query]);

  const onScroll = useCallback(function() {
    if (isAnySuggestionType(SuggestionsType.LOOKUP)(menuSelectionType)) {
      const {scrollTop, scrollHeight, clientHeight} = menuListRef.current;
      if (scrollTop + clientHeight >= scrollHeight - 200) {
        refreshLast(true);
      }
    }
  }, [menuSelectionType, menuListRef]);

  const onKeyPress = useCallback(function(event) {
    if (isTab(event)) {
      event.preventDefault();
      inputEventTrigger(selectedSuggestions?.length > 0);
    }
    if (isEscape(event)) {
      inputEventTrigger(false);
      closeDropdown();
      setShowSuggestions(false);
    }
  }, [selectedSuggestions]);

  if (isAnySuggestionType(SuggestionsType.ATTRIBUTE)(menuSelectionType) && listOptions.length === 0) {
    return null;
  }

  return (
    <MenuList id={MenuListID}
      ref={menuListRef}
      autoFocus={menuAutoFocus}
      variant={'menu'}
      onKeyDown={onKeyPress}
      classes={{root: classes.listWrapper}}
      onScroll={onScroll}>
      {isAnySuggestionType(SuggestionsType.SELECTION,
        SuggestionsType.MULTISELECTION,
        SuggestionsType.DATE)(menuSelectionType) && (
        <MultiSelectionSuggestions onSelection={onSelection}
          classes={classes}
          hasDateField={hasDateField}
          isMultiSelection={isAnySuggestionType(SuggestionsType.MULTISELECTION)(menuSelectionType)}
          selectedSuggestions={selectedSuggestions}
          listOptions={listOptions}
          setShowSuggestions={setShowSuggestions}
          menuSelectionType={menuSelectionType} />
      )}
      {isAnySuggestionType(SuggestionsType.LOOKUP)(menuSelectionType) && (
        <DynamicLookUp lookUp={lookUp}
          attributeMappings={attributeMappings}
          onSelection={onSelection}
          dropdownSearch={inputValue}
          isLast={isLast}
          isCaseInsensitive={isCaseInsensitive}
          refreshLast={refreshLast}
          updateFilterLookUpData={updateFilterLookUpData}
          accountTree={accountTree} />
      )}
    </MenuList>
  );
};
